<script setup>
import {defineModel, defineProps, ref, defineEmits, computed, onUnmounted} from 'vue'
import GoogleSheetsBrandingSvg from "@/components/UI/svg/GoogleSheetsBrandingSvg.vue";
import GoogleSheetsDestinationSvg from "@/components/UI/svg/GoogleSheetsDestinationSvg.vue";
import TableSelect from "@/components/Connection/FileSelect.vue";
import CrossSvg from "@/components/UI/CrossSvg.vue";
import CrossCloseNotifySvg from "@/components/UI/svg/CrossCloseNotifySvg.vue";
import {isStringEmpty} from "@/service/utils";
import LoadingSvg from "@/components/UI/LoadingSvg.vue";
import GoogleDriveBrandingSvg from "@/components/UI/svg/GoogleDriveBrandingSvg.vue";
import {Api} from "@/service/api";


const processorTypeToName = {
    'SOURCE': 'Источник',
    'DESTINATION': 'Назначение',
}

const updateLabel = "Закончить редактирование"
const createLabel = "Создать соединение"

const props = defineProps({
    processorType: {
        type: String,
        required: true,
        validator: (value) => {
            return ['SOURCE', 'DESTINATION'].includes(value)
        }
    },
    isLoading: {
        type: Boolean,
        default: false
    },
    isCreateButtonActive: {
        type: Boolean,
        default: false
    },
    isCreate: {
        type: Boolean,
        default: true
    }
})

const file = defineModel('file')

const emits = defineEmits(['reload-tabs', 'open-modal', 'add-source', 'change-step', 'next-step', 'save-files'])

const isToggle = defineModel('isToggle')

const isGoogleAccountLoading = ref(false)

const checkInterval = ref(null)

const isTableSelected = computed(() => {
    return !(isStringEmpty(file.value.id) || isStringEmpty(file.value.tab.sheetId));

})

const isSubmited = ref(false)


function toggleBlock() {
    isToggle.value = false
}

function openModal() {
    emits('open-modal')
}

function reloadTabs() {
    emits("reload-tabs")
}


function changeStep() {
    isSubmited.value = false
    emits('change-step');
}

function nextStep() {
    isSubmited.value = true
    emits('next-step')
}

function saveFiles(values, index) {
    emits("save-files", values[0], index)
}

const isAccountAttached = defineModel('isAccountAttached')


onUnmounted(() => {
    if (checkInterval.value) {
        clearInterval(checkInterval.value)
    }
})


async function attachGoogleAccount() {
    isGoogleAccountLoading.value = true
    const url = await Api.getGoogleOauthUrl()


    window.after_authorization = async function() {
        checkInterval.value = setInterval(async () => {
            try {
                await Api.getUserGoogleAccessToken()
                isGoogleAccountLoading.value = false
                clearInterval(checkInterval.value)
                isAccountAttached.value = true
            }
            catch (e) {
                if (e.response.status === 403) {
                    isAccountAttached.value = false
                }
            }
        }, 500)
    }

    window.open(url, '_blank', "top=100, left=100, width=400, height=500")

}

</script>

<template>
    <div class="w-full">
        <div
            v-show="isToggle"
            v-on:click="toggleBlock"
            class="w-full bg-white rounded-lg h-32 flex items-center justify-between shadow-xl border-2 border-transparent cursor-pointer hover:border-blue-500"
        >
            <div class="flex items-center justify-center">
                <GoogleSheetsBrandingSvg v-if="props.processorType === 'SOURCE'" class="w-20 ml-5 mr-2" />
                <GoogleSheetsDestinationSvg v-if="props.processorType === 'DESTINATION'" class="w-20 ml-5 mr-2" />
                <div>
                    <div class="font-bold text-xl">
                        {{ processorTypeToName[props.processorType] }}
                    </div>
                    <div>
                        Google Sheets
                    </div>
                </div>
            </div>
            <div
                class="mr-10 size-10 flex items-center justify-center rounded-full hover:bg-gray-200 cursor-pointer transition-all duration-150"
                v-on:click="changeStep"
            >
                <CrossSvg class="size-8"/>
            </div>
        </div>
        <div
            v-show="!isToggle"
            class="w-full flex flex-col justify-center items-start bg-white rounded-lg shadow-xl"
        >
            <div class="flex items-center justify-between w-full">
                <div class="flex items-center justify-center h-32">
                    <GoogleSheetsBrandingSvg v-if="props.processorType === 'SOURCE'" class="w-20 ml-5 mr-2" />
                    <GoogleSheetsDestinationSvg v-if="props.processorType === 'DESTINATION'" class="w-20 ml-5 mr-2" />
                    <div>
                        <div class="font-bold text-xl">
                            {{ processorTypeToName[props.processorType] }}
                        </div>
                        <div>
                            Google Sheets
                        </div>
                    </div>
                </div>
                <div
                    class="mr-10 size-10 flex items-center justify-center rounded-full hover:bg-gray-200 cursor-pointer transition-all duration-150"
                    v-on:click="changeStep"
                >
                    <CrossCloseNotifySvg class="size-8"/>
                </div>
            </div>
            <div class="w-full flex flex-col items-center mb-10 border-t border-gray-200" v-show="isAccountAttached" >
                <TableSelect
                    :index="0"
                    v-model:file="file"
                    @reload-tabs="reloadTabs"
                    @open-modal="openModal"
                    @save-files="saveFiles"
                />
            </div>
            <div v-show="!isAccountAttached" class="w-full flex flex-col items-center mb-10 border-t border-gray-200">
                <div class="w-4/5 border-blue-500 border bg-blue-50 border-dashed rounded-lg min-h-fit max-h-fit my-10 flex-col items-center justify-center">
                    <GoogleDriveBrandingSvg class="size-10 mx-auto my-5" />
                    <div class="text-base mx-auto max-w-fit min-w-fit text-gray-500 my-3">
                        Выберите файлы из гугл диска
                    </div>
                    <div
                        v-on:click="attachGoogleAccount"
                        v-if="!isGoogleAccountLoading"
                        class="text-sm mx-auto max-w-fit min-w-fit font-bold text-white py-3 rounded-full bg-blue-500 px-3 mb-5 select-none cursor-pointer hover:bg-blue-600 duration-150 transition-all"
                    >
                        Подключите Google Drive
                    </div>
                    <div
                        v-else
                        class="text-sm mx-auto max-w-fit min-w-fit font-bold text-white py-3 rounded-full bg-blue-500 px-3 mb-5 select-none cursor-pointer hover:bg-blue-600 duration-150 transition-all"
                    >
                        <LoadingSvg class="size-6 animate-spin"/>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="fixed left-1/2 -translate-x-1/2 sm:left-auto sm:right-4 md:right-6 lg:right-10 bottom-4 sm:bottom-5 px-5 py-3 rounded-full cursor-default select-none bg-gray-400 font-bold text-gray-600 z-30"
            v-show="!isToggle"
            v-if="!isTableSelected || !isCreateButtonActive"
        >
            {{ isCreate ? createLabel : updateLabel }}

        </div>
        <div
            class="flex items-center justify-center fixed left-1/2 -translate-x-1/2 sm:left-auto sm:right-4 md:right-6 lg:right-10 bottom-4 sm:bottom-5 px-5 rounded-full bg-blue-500 hover:bg-blue-600 duration-150 transition-all cursor-pointer select-none font-bold text-white z-30"
            v-on:click="nextStep"
            v-show="!isToggle"
            v-else
        >
            <div class="w-52 flex items-center justify-center h-10" v-if="!isLoading">
                {{ isCreate ? createLabel : updateLabel }}

            </div>
            <div class="w-52 flex items-center justify-center h-10" v-else>
                <LoadingSvg class="size-6 animate-spin" />
            </div>
        </div>
    </div>

</template>

<style scoped>

</style>